import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { inr, when } from './fmt';

// Plain month list (Safari has no native month picker) — FY 2026-27 onwards, up to 12 months ahead
const MONTHS = (() => {
  const out = [];
  const end = new Date(); end.setMonth(end.getMonth() + 12);
  for (let d = new Date(2026, 3, 1); d <= end; d.setMonth(d.getMonth() + 1)) {
    out.push({ v: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, l: d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) });
  }
  return out;
})();

const AddSalary = ({ t, onSaved }) => {
  const [amt, setAmt] = useState('');
  const [eff, setEff] = useState(new Date().toISOString().slice(0, 7));
  const [note, setNote] = useState('');
  const save = async () => {
    try {
      await api.post('/trainer-targets/salaries', { trainer_id: t.id, monthly_salary: amt, effective_from: eff, note });
      toast.success(`Salary saved for ${t.name}`); setAmt(''); setNote(''); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Input type="number" value={amt} onChange={e => setAmt(e.target.value)} placeholder="Monthly ₹" className="h-7 w-28 text-xs" data-testid={`tt-salary-amount-${t.id}`} />
      <select value={eff} onChange={e => setEff(e.target.value)} className="h-7 border border-slate-200 rounded-md text-xs px-1 bg-white" data-testid={`tt-salary-month-${t.id}`}>
        {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
      </select>
      <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Note (e.g. increment)" className="h-7 w-40 text-xs" data-testid={`tt-salary-note-${t.id}`} />
      <Button size="sm" className="h-7 text-[11px]" disabled={!amt || !eff} onClick={save} data-testid={`tt-salary-save-${t.id}`}>Add</Button>
    </div>
  );
};

export const SalaryTab = ({ source, onSaved }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get('/trainer-targets/salaries').then(r => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const saved = () => { load(); onSaved(); };
  const sync = async () => {
    setBusy(true);
    try { const r = await api.post('/trainer-targets/salaries/sync'); toast.success(`Synced: ${r.data.updated} updated, ${r.data.unmatched} unmatched`); saved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); } finally { setBusy(false); }
  };
  if (!d) return <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const manual = source === 'manual';
  return (
    <div className="space-y-3" data-testid="tt-salary-tab">
      <Card className="p-3 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="text-slate-600" data-testid="tt-salary-source">{manual
          ? 'Salaries are entered here for now. Once the salary app is connected they will be pulled from it automatically and manual entry will switch off.'
          : 'Salaries come from the salary app.'}</span>
        {!manual && <Button size="sm" variant="outline" onClick={sync} disabled={busy} data-testid="tt-salary-sync"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy ? 'animate-spin' : ''}`} />Sync now</Button>}
      </Card>
      <Card className="divide-y divide-slate-100">
        {d.rows.map(t => (
          <div key={t.id} className="p-3 grid md:grid-cols-12 gap-2 items-start" data-testid={`tt-salary-row-${t.id}`}>
            <div className="md:col-span-3">
              <div className="text-sm font-medium text-slate-900">{t.name}{t.status !== 'active' && <span className="ml-1 text-[10px] text-slate-400">(inactive)</span>}</div>
              <div className="text-xs text-slate-500">Current: <b data-testid={`tt-salary-current-${t.id}`}>{inr(t.current_salary)}</b>/month</div>
            </div>
            <div className="md:col-span-4 text-[11px] text-slate-500 space-y-0.5">
              {t.entries.length === 0 ? <span className="text-amber-600">No salary yet — no target can be set</span> : t.entries.map(e => (
                <div key={e.id}>{inr(e.monthly_salary)} from {e.effective_from} · {e.source === 'api' ? 'Salary app' : e.set_by} · {when(e.created_at)}{e.note ? ` · ${e.note}` : ''}</div>
              ))}
            </div>
            <div className="md:col-span-5">{manual && <AddSalary t={t} onSaved={saved} />}</div>
          </div>
        ))}
      </Card>
    </div>
  );
};
